import type { CSSProperties } from "react"
import React, { useId, useLayoutEffect, useRef, useState } from "react"
import { useTooltip } from "../../../../hooks/useTooltip"
import { Sizes } from "../../../../interfaces/sizing"
import { mergeClasses } from "../../../../utils/css"
import { CheckmarkSlim } from "../Icons/Others"
import { Label } from "../Label"
import { Tooltip } from "../Tooltip"
import { Typography } from "../Typography"
import "./Checkbox.scss"

interface CheckboxProps extends React.ComponentPropsWithRef<"input"> {
    label?: string
    checked?: boolean
    className?: string
    tooltipText?: string
    onToggle: (checked: boolean) => void
}
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            label,
            checked,
            className,
            tooltipText,
            onToggle,
            ...props
        }: CheckboxProps,
        ref,
    ) => {
        const uniqueId = useId()
        const labelRef = useRef(null)
        const { isOpen } = useTooltip(labelRef)
        const [tooltipPosition, setTooltipTooltipPosition] = useState(
            {} as CSSProperties,
        )

        useLayoutEffect(() => {
            if (labelRef.current) {
                setTooltipTooltipPosition({
                    position: "absolute",
                    top: `-40px`,
                    left: `${(labelRef.current as HTMLElement).clientWidth / 2 + 15}px`,
                    transform: "translate(-50%, 0)",
                    width: "max-content",
                } as CSSProperties)
            }
        }, [isOpen])

        return (
            <>
                <div className={mergeClasses("Checkbox", className)} ref={ref}>
                    <Label
                        size={Sizes.smpx}
                        className={mergeClasses(
                            "Checkbox__label-container",
                            props.disabled ? "Checkbox__disabled" : "",
                        )}
                        htmlFor={`Checkbox__checkbox_${uniqueId}`}
                        ref={labelRef}
                    >
                        <div
                            className={`Checkbox__checkbox-container ${checked ? `checked` : ``}`}
                        >
                            <input
                                type="checkbox"
                                id={`Checkbox__checkbox_${uniqueId}`}
                                onChange={(e) => onToggle(e.target.checked)}
                                checked={checked}
                                {...props}
                            />
                            <CheckmarkSlim
                                className={`Checkbox__checkmark ${checked ? `checked` : ``}`}
                            />
                        </div>
                        {label && (
                            <Typography
                                component="p"
                                size={Sizes.smpx}
                                className="Checkbox__label"
                            >
                                {label}
                            </Typography>
                        )}
                    </Label>
                    {tooltipText && isOpen && (
                        <Tooltip text={tooltipText} style={tooltipPosition} />
                    )}
                </div>
            </>
        )
    },
)
